import { z } from 'zod'

import type { FieldTypeEnum } from './custom-field.types'
import type { FormFieldType } from './forms'
import {
  ConditionActionEnum,
  ConditionLogicEnum,
  ConditionOperatorEnum,
  FieldOptionSchema,
  FieldValidationSchema,
  FormFieldTypeEnum,
  FormTargetTypeEnum,
} from './forms'

// =====================
// Form field → custom-field type mapping
// =====================
//
// Used by the form→collection sync: each data field on a form-managed
// collection gets a CustomFieldDefinition of the equivalent type. Layout
// fields (and anything without a sensible custom-field analogue) map to null
// and are skipped by the sync.
const FORM_FIELD_TO_CUSTOM_FIELD_TYPE: Partial<
  Record<FormFieldType, FieldTypeEnum>
> = {
  SHORT_TEXT: 'STRING',
  LONG_TEXT: 'TEXT',
  RICH_TEXT: 'RICH_TEXT',
  EMAIL: 'STRING',
  PHONE: 'STRING',
  URL: 'LINK',
  ADDRESS: 'ADDRESS',
  NUMBER: 'NUMBER',
  PERCENTAGE: 'PERCENTAGE',
  CURRENCY: 'CURRENCY',
  DATE: 'DATE',
  DATETIME: 'DATETIME',
  TIME: 'TIME',
  DROPDOWN: 'DROPDOWN',
  MULTISELECT: 'MULTISELECT',
  CHECKBOX: 'MULTISELECT',
  RADIO: 'DROPDOWN',
  BOOLEAN: 'BOOLEAN',
  RATING: 'NUMBER',
  FILE_UPLOAD: 'FILE',
  MULTI_FILE_UPLOAD: 'ATTACHMENTS',
  REFERENCE: 'REFERENCE',
  MULTI_REFERENCE: 'MULTI_REFERENCE',
}

export function mapFormFieldTypeToCustomFieldType(
  fieldType: FormFieldType,
): FieldTypeEnum | null {
  return FORM_FIELD_TO_CUSTOM_FIELD_TYPE[fieldType] ?? null
}

// =====================
// AI form generation
// =====================
//
// The AI emits a form definition where fields reference each other by NAME
// (real field uuids don't exist until fields are persisted / added to the
// builder). The applier — builder canvas or create flow — resolves names to
// ids. This mirrors the workflow-generation contract: the endpoint returns
// { definition | null, valid, issues[] } and never throws on bad AI output.

export const AiFormConditionSchema = z.object({
  // Name of another field on the form (AiFormFieldSchema.name)
  fieldName: z.string().min(1),
  operator: ConditionOperatorEnum,
  value: z.unknown().optional(),
})
export type AiFormCondition = z.infer<typeof AiFormConditionSchema>

export const AiConditionalRuleSchema = z.object({
  action: ConditionActionEnum,
  logic: ConditionLogicEnum,
  conditions: z.array(AiFormConditionSchema).min(1),
})
export type AiConditionalRule = z.infer<typeof AiConditionalRuleSchema>

export const AiFormFieldSchema = z.object({
  // Stable machine name; also the reconciliation key when editing an
  // existing form (same name = same field).
  // Existing builder fields can carry camelCase names (target-field drags use
  // the mapping key as the name), so the round-trip schema accepts any word
  // characters — the prompt still asks the model for snake_case on new fields.
  name: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-zA-Z0-9_-]+$/, 'letters/numbers/underscores/hyphens only'),
  label: z.string().min(1).max(255),
  fieldType: FormFieldTypeEnum,
  description: z.string().nullable().optional(),
  placeholder: z.string().max(255).nullable().optional(),
  helpText: z.string().nullable().optional(),
  isRequired: z.boolean().optional(),
  options: z.array(FieldOptionSchema).nullable().optional(),
  validation: FieldValidationSchema.nullable().optional(),
  width: z.enum(['full', 'half', 'third']).optional(),
  // Standard target-field key (e.g. 'subject', 'requesterEmail') or omitted
  // for a custom field. Validated server-side against the target module's
  // known standard fields.
  mappedField: z.string().max(100).nullable().optional(),
  conditions: z.array(AiConditionalRuleSchema).nullable().optional(),
})
export type AiFormField = z.infer<typeof AiFormFieldSchema>

export const AiFormDefinitionSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  fields: z.array(AiFormFieldSchema).min(1).max(60),
})
export type AiFormDefinition = z.infer<typeof AiFormDefinitionSchema>

// =====================
// Request / response DTOs
// =====================

export const GenerateFormRequestSchema = z.object({
  prompt: z.string().min(1).max(4000),
  targetType: FormTargetTypeEnum.optional(),
  targetModuleType: z.string().optional(),
  // Present when editing: the current form definition, serialized to the AI
  // shape (ids replaced by names) by the caller.
  existingDefinition: AiFormDefinitionSchema.optional(),
})
export type GenerateFormRequest = z.infer<typeof GenerateFormRequestSchema>

export interface FormGenerationIssue {
  path?: string
  message: string
}

export interface GeneratedFormResult {
  name: string | null
  description: string | null
  definition: AiFormDefinition | null
  valid: boolean
  issues: FormGenerationIssue[]
}
